import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { useAppContext } from "@/context/AppContext.tsx";

interface RatingData {
  bewertung_stars: number;
  bewertung_anzahl: number;
}

interface RatingContextType {
  rating: number;
  ratingNumber: number;
  isVoting: boolean;
  isLoading: boolean;
  setIsVoting: React.Dispatch<React.SetStateAction<boolean>>;
  handleVote: (index: number) => Promise<void>;
  refreshRating: () => Promise<void>;
}

interface RatingProviderProps {
  children: ReactNode;
  recipeId: string | number;
  initialStars?: number;
  initialCount?: number;
  invalidateReviewCache?: (recipeId: number) => void;
}

const RatingContext = createContext<RatingContextType | undefined>(undefined);

export const RatingProvider: React.FC<RatingProviderProps> = ({
  children,
  recipeId,
  initialStars = 0,
  initialCount = 0,
  invalidateReviewCache,
}) => {
  const [rating, setRating] = useState<number>(initialStars);
  const [ratingNumber, setRatingNumber] = useState<number>(initialCount);
  const [isVoting, setIsVoting] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { user } = useAppContext();

  // Funktion zum Abrufen der aktuellen Daten
  const refreshRating = useCallback(async () => {
    try {
      const response = await fetch(
        `/api/recipes?type=starRating&recipeId=${recipeId}`,
      );
      if (!response.ok) return;

      const res: RatingData = await response.json();
      if (
        res.bewertung_anzahl !== undefined &&
        res.bewertung_stars !== undefined
      ) {
        setRating(res.bewertung_stars);
        setRatingNumber(res.bewertung_anzahl);
      }
    } catch (error) {
      console.warn("Rating konnte nicht aktualisiert werden:", error);
    }
  }, [recipeId]);

  // Der Vote-Handler
  const handleVote = async (index: number) => {
    setIsVoting(true);
    setIsLoading(true);
    try {
      // Optionaler Cache-Invalidierungscall
      if (invalidateReviewCache) {
        if (typeof recipeId === "string") {
          invalidateReviewCache(parseInt(recipeId));
        } else {
          invalidateReviewCache(recipeId);
        }
      }

      const response = await fetch(
        `/api/recipes?type=voting&bewertung_stars=${index + 1}&rezept_id=${recipeId}&user_id=${user?.id || ""}`,
        { method: "POST" },
      );

      if (!response.ok) throw new Error("Fehler beim Voting");

      const res: RatingData = await response.json();

      if (res) {
        setRating(res.bewertung_stars);
        setRatingNumber(res.bewertung_anzahl);
      }
    } catch (e) {
      console.error("Voting fehlgeschlagen:", e);
    } finally {
      setIsVoting(false);
      setIsLoading(false);
    }
  };

  // Initialer Fetch wenn sich die recipeId ändert
  useEffect(() => {
    refreshRating();
  }, [refreshRating]);

  const value: RatingContextType = {
    rating,
    ratingNumber,
    isVoting,
    isLoading,
    setIsVoting,
    handleVote,
    refreshRating,
  };

  return (
    <RatingContext.Provider value={value}>{children}</RatingContext.Provider>
  );
};

// --- Custom Hook ---

export const useRating = (): RatingContextType => {
  const context = useContext(RatingContext);
  if (!context) {
    throw new Error(
      "useRating muss innerhalb eines RatingProviders verwendet werden",
    );
  }
  return context;
};
